import type {
  AdminUserDetailDto,
  AdminUserListItemDto,
  AdminUserRole,
  AdminUserStatus,
  AdminUserKycStatus,
  AdminUserStatusChange,
  ListAdminUsersResultDto,
} from '@carcheck/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hooks for the admin user-management surface (AUT-124 /
 * ADMIN-12), targeting the AUT-113 backend:
 *
 *   - `GET   /admin/users`            (cursor-paginated)
 *   - `GET   /admin/users/:id`
 *   - `PATCH /admin/users/:id/status`
 *
 * Query-key convention (ADMIN-10 guardrail / queryClient.ts):
 *   list   → `['admin', 'users', 'list', filters]`
 *   detail → `['admin', 'users', 'detail', id]`
 * Mutations invalidate by the `['admin', 'users']` prefix on success.
 *
 * Server state lives in TanStack; the auth/session permission set lives in
 * MobX. The two never duplicate a datum.
 */

const USERS_BASE = '/admin/users';

/** Stable query-key prefix; mutations invalidate against this. */
export const ADMIN_USERS_KEY = ['admin', 'users'] as const;

/**
 * Filter / pagination params for the list query. Mirrors the backend
 * `ListAdminUsersQuerySchema` — `status` is a multi-select; `role` /
 * `kycStatus` are single; `q` is a min-3-char free-text search (enforced
 * client-side before it reaches here). `cursor` / `limit` drive the
 * cursor pagination.
 */
export interface AdminUsersListParams {
  role?: AdminUserRole;
  status?: AdminUserStatus[];
  kycStatus?: AdminUserKycStatus;
  q?: string;
  cursor: string | null;
  limit: number;
}

/** Build the URLSearchParams a list fetch sends (omits empty values). */
const buildListParams = (params: AdminUsersListParams): Record<string, string> => {
  const query: Record<string, string> = { limit: String(params.limit) };
  if (params.role) query.role = params.role;
  if (params.status && params.status.length > 0) query.status = params.status.join(',');
  if (params.kycStatus) query.kycStatus = params.kycStatus;
  if (params.q && params.q.length > 0) query.q = params.q;
  if (params.cursor) query.cursor = params.cursor;
  return query;
};

export const useAdminUsersList = (
  params: AdminUsersListParams,
): UseQueryResult<ListAdminUsersResultDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_USERS_KEY, 'list', params],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<ListAdminUsersResultDto>(USERS_BASE, {
        params: buildListParams(params),
        signal,
      });
      return data;
    },
  });

export const useAdminUserDetail = (id: string | null): UseQueryResult<AdminUserDetailDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_USERS_KEY, 'detail', id],
    enabled: id !== null,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminUserDetailDto>(`${USERS_BASE}/${id}`, { signal });
      return data;
    },
  });

/** Envelope returned by `PATCH /admin/users/:id/status` (read `.dto`). */
interface SetUserStatusResponse {
  dto: AdminUserListItemDto;
  previousStatus: string;
}

export interface SetUserStatusVariables {
  id: string;
  status: AdminUserStatusChange;
  reason: string;
}

/**
 * Low-level status mutation. Role + permission + audit gated server-side
 * (AUT-138 removed per-action TOTP step-up).
 */
const patchUserStatus = async (
  variables: SetUserStatusVariables,
): Promise<AdminUserListItemDto> => {
  const { id, status, reason } = variables;
  const { data } = await apiClient.patch<SetUserStatusResponse>(`${USERS_BASE}/${id}/status`, {
    status,
    reason,
  });
  return data.dto;
};

/**
 * Hook factory binding the status mutation to an action callback. On
 * success it invalidates the `['admin','users']` prefix so the list + the
 * open detail refetch with the new status.
 *
 * Returns `{ action, invalidate }`:
 *   - `action` runs the status PATCH.
 *   - `invalidate` is the cache-invalidation callback the page calls after
 *     the action resolves.
 */
export const useSetUserStatusAction = (): {
  action: (variables: SetUserStatusVariables) => Promise<AdminUserListItemDto>;
  invalidate: () => Promise<void>;
} => {
  const queryClient = useQueryClient();
  return {
    action: patchUserStatus,
    invalidate: () => queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
  };
};

/**
 * Convenience `useMutation` wrapper for the status PATCH (e.g. tests, or a
 * future non-drawer caller).
 */
export const useSuspendUser = (): UseMutationResult<
  AdminUserListItemDto,
  Error,
  SetUserStatusVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => patchUserStatus(variables),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY }),
  });
};
